import React from "react";
import { Input } from "reactstrap";
import configure from "configure";

const key = (serviceId, fi, si, role) => `${serviceId}|${fi}|${si}|${role}`;

const ROLES = ["dev", "ba", "qa"];
const ROLE_LABELS = { dev: "Dev", ba: "BA", qa: "QA" };

const getHrs = (timing, k) => timing[k] || 0;

// Sum a single role across all sub-features of a feature
const featureRoleTotal = (timing, serviceId, fi, subCount, role) =>
  Array.from({ length: subCount }, (_, si) =>
    getHrs(timing, key(serviceId, fi, si, role)),
  ).reduce((a, b) => a + b, 0);

// All-role total for a feature
const featureTotal = (timing, serviceId, fi, subCount) =>
  ROLES.reduce(
    (acc, role) =>
      acc + featureRoleTotal(timing, serviceId, fi, subCount, role),
    0,
  );

// All-role total for a service
const serviceTotal = (timing, serviceId, subtitles) =>
  subtitles.reduce(
    (acc, sub, fi) =>
      acc + featureTotal(timing, serviceId, fi, sub.features.length),
    0,
  );

// Grand dev-only total (used for BA/QA auto or display)
const grandRoleTotal = (timing, features, role) =>
  features.reduce(
    (acc, s) =>
      acc +
      s.subtitles.reduce(
        (a2, sub, fi) =>
          a2 +
          featureRoleTotal(timing, s.serviceId, fi, sub.features.length, role),
        0,
      ),
    0,
  );

const grandTotal = (timing, features) =>
  ROLES.reduce((acc, role) => acc + grandRoleTotal(timing, features, role), 0);

const StepTiming = ({
  features,
  timing,
  setTiming,
  services,
  pricingMode,
  extraHours,
  setExtraHours,
  readOnly,
  stepLogs,
  allUsers,
}) => {
  const updateHours = (serviceId, fi, si, role, value) =>
    setTiming((prev) => ({
      ...prev,
      [key(serviceId, fi, si, role)]: Number(value) || 0,
    }));

  const isApprovalStillValid = (logs) => {
    if (!logs?.approved?.length) return false;
    if (!logs?.submitted?.length) return true; // approved, never re-submitted
    const lastApprovedAt = new Date(logs.approved.at(-1).at);
    const lastSubmittedAt = new Date(logs.submitted.at(-1).at);
    return lastApprovedAt > lastSubmittedAt; // approved AFTER last submission
  };

  const serviceMap = Object.fromEntries(
    (services || []).map((s) => [s.value, s]),
  );

  const devTotal = grandRoleTotal(timing, features, "dev");
  const baTotal = grandRoleTotal(timing, features, "ba");
  const qaTotal = grandRoleTotal(timing, features, "qa");
  const grandFinal = devTotal + baTotal + qaTotal;

  return (
    <div className="rfp-features">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="rfp-title mb-0">Project Timing</h4>
        <div className="rfp-timing-pill">Total: {grandFinal} hrs</div>
      </div>

      {/* ── Per-service feature grids ── */}
      {features.map((svc) => {
        const svcObj = serviceMap[svc.serviceId];
        const svcName = svcObj?.title?.trim() || svcObj?.label || svc.serviceId;
        const svcHrs = serviceTotal(timing, svc.serviceId, svc.subtitles);

        return (
          <div key={svc.serviceId} className="mb-4">
            <div className="rfp-service-header">
              <h5 className="rfp-service-title mb-0">{svcName}</h5>
              <span className="rfp-timing-pill">{svcHrs} hrs</span>
            </div>

            <div className="rfp-feature-grid mt-3">
              {svc.subtitles.map((feature, fi) => {
                const fTotal = featureTotal(
                  timing,
                  svc.serviceId,
                  fi,
                  feature.features.length,
                );

                return (
                  <div key={fi} className="rfp-inner-card">
                    <div className="rfp-inner-header">
                      <span className="rfp-badge">{fi + 1}</span>
                      <span
                        className="fw-semibold"
                        style={{ fontSize: "13px" }}
                      >
                        {feature.title || (
                          <em className="text-muted">Untitled Feature</em>
                        )}
                      </span>
                      <span className="rfp-timing-pill ms-auto">
                        {fTotal} hrs
                      </span>
                    </div>

                    <div className="rfp-inner-body">
                      {feature.features.length === 0 ? (
                        <div className="rfp-empty-sm">No sub-features.</div>
                      ) : (
                        <>
                          {/* Column header */}
                          <div className="rfp-col-header">
                            <span style={{ minWidth: "24px" }}>#</span>
                            <span style={{ flex: 1 }}>Sub-feature</span>
                            {ROLES.map((role) => (
                              <span key={role} className="rfp-role-col-label">
                                {ROLE_LABELS[role]}
                              </span>
                            ))}
                          </div>

                          {/* Sub-feature rows */}
                          {feature.features.map((sf, si) => (
                            <div key={si} className="rfp-subfeature-row">
                              <span className="rfp-row-index">{si + 1}.</span>
                              <span style={{ flex: 1, fontSize: "12px" }}>
                                {sf.title || (
                                  <em className="text-muted">Untitled</em>
                                )}
                              </span>
                              {ROLES.map((role) => (
                                <Input
                                  key={role}
                                  bsSize="sm"
                                  type="number"
                                  min={0}
                                  disabled={readOnly}
                                  value={
                                    getHrs(
                                      timing,
                                      key(svc.serviceId, fi, si, role),
                                    ) || ""
                                  }
                                  placeholder="0"
                                  // onChange={e =>
                                  //   updateHours(svc.serviceId, fi, si, role, e.target.value)
                                  // }
                                  onChange={(e) =>
                                    !readOnly &&
                                    updateHours(
                                      svc.serviceId,
                                      fi,
                                      si,
                                      role,
                                      e.target.value,
                                    )
                                  }
                                  className="rfp-role-input"
                                  title={`${ROLE_LABELS[role]} hours`}
                                />
                              ))}
                            </div>
                          ))}

                          {/* Feature-level role subtotals */}
                          <div className="rfp-feature-role-totals">
                            <span
                              style={{
                                flex: 1,
                                fontSize: "11px",
                                color: "#aaa",
                              }}
                            >
                              Feature subtotal
                            </span>
                            {ROLES.map((role) => (
                              <span key={role} className="rfp-role-subtotal">
                                {featureRoleTotal(
                                  timing,
                                  svc.serviceId,
                                  fi,
                                  feature.features.length,
                                  role,
                                )}
                              </span>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* ── Hours summary ── */}
      {features.length > 0 && (
        <div className="rfp-hours-summary">
          {/* Dev Hours */}
          <div className="rfp-hours-row">
            <div className="rfp-hours-row-label">
              <span>Developer Hours</span>
              <span className="rfp-hours-auto-tag">auto-calculated</span>
            </div>
            <span className="rfp-hours-value">{devTotal} hrs</span>
          </div>

          {/* BA Hours */}
          <div className="rfp-hours-row">
            <div className="rfp-hours-row-label">
              <span>BA Hours</span>
              <span className="rfp-hours-auto-tag">auto-calculated</span>
            </div>
            <span className="rfp-hours-value">{baTotal} hrs</span>
          </div>

          {/* QA Hours */}
          <div className="rfp-hours-row">
            <div className="rfp-hours-row-label">
              <span>QA Hours</span>
              <span className="rfp-hours-auto-tag">auto-calculated</span>
            </div>
            <span className="rfp-hours-value">{qaTotal} hrs</span>
          </div>

          {/* Grand Total */}
          <div className="rfp-total-bar mt-0">
            <span className="rfp-total-label">Grand Total</span>
            <span className="rfp-total-value">{grandFinal} hrs</span>
          </div>
        </div>
      )}

      {/* {stepLogs &&
        (stepLogs.saved?.length > 0 ||
          stepLogs.submitted?.length > 0 ||
          stepLogs.approved?.length > 0) && (
          <div className="rfp-activity-log">
            <div className="rfp-activity-log-title">Activity</div>
            <div className="d-flex gap-3 flex-wrap">
              {stepLogs.saved?.length > 0 && (
                <div className="rfp-activity-pill">
                  <div className="rfp-activity-icon">
                    {(() => {
                      const user = allUsers?.find(
                        (u) => u.id == stepLogs.saved[0].by,
                      );
                      return user?.avatar ? (
                        <img
                          src={`${configure.apiUrl}${user.avatar}`}
                          alt={user.name}
                          style={{
                            width: 28,
                            height: 28,
                            borderRadius: "50%",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <i className="fas fa-user" />
                      );
                    })()}
                  </div>
                  <div>
                    <div className="rfp-activity-label">Created By</div>
                    <div className="rfp-activity-user">
                      {allUsers?.find((u) => u.id == stepLogs.saved[0].by)
                        ?.name || `User #${stepLogs.saved[0].by}`}
                    </div>
                    <div className="rfp-activity-time">
                      {new Date(stepLogs.saved[0].at).toLocaleString()}
                    </div>
                  </div>
                </div>
              )}

              {isApprovalStillValid(stepLogs) && (
                <div className="rfp-activity-pill rfp-activity-pill--approved">
                  <div className="rfp-activity-icon rfp-activity-icon--approved">
                    {(() => {
                      const user = allUsers?.find(
                        (u) => u.id == stepLogs.saved[0].by,
                      );
                      return user?.avatar ? (
                        <img
                          src={`${configure.apiUrl}${user.avatar}`}
                          alt={user.name}
                          style={{
                            width: 28,
                            height: 28,
                            borderRadius: "50%",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <i className="fas fa-user" />
                      );
                    })()}
                  </div>
                  <div>
                    <div className="rfp-activity-label">Approved by</div>
                    <div className="rfp-activity-user">
                      {allUsers?.find((u) => u.id == stepLogs.saved[0].by)
                        ?.name || `User #${stepLogs.saved[0].by}`}
                    </div>
                    <div className="rfp-activity-time">
                      {new Date(stepLogs.approved.at(-1).at).toLocaleString()}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )} */}
        {stepLogs && (
  <div className="rfp-activity-log mt-4">
    <div className="rfp-activity-log-title">Activity</div>
    <div className="d-flex gap-3 flex-wrap">
      {(() => {
        const cards = [
  {
    label: "Created By",
    sublabel: null,
    entry: stepLogs.step2?.saved?.[0],
    active: !!stepLogs.step2?.saved?.[0],
  },
  {
    label: "Level 1",
    sublabel: "(Timing)",
    entry: stepLogs.step3?.submitted?.at(-1),
    active:
      !!stepLogs.step3?.submitted?.at(-1) &&
      (
        stepLogs.step3?.approved?.length === 0 ||   // submitted, awaiting approval
        !!stepLogs.step3?.approved?.at(-1)           // already approved
      ),
  },
  {
    label: "Level 2",
    sublabel: "(Costing)",
    entry: stepLogs.step4?.saved?.at(-1),
    active:
      !!stepLogs.step4?.saved?.at(-1) &&
      !!stepLogs.step3?.approved?.at(-1),
  },
  {
    label: "Final Approval",
    sublabel: "(Overview)",
    entry: stepLogs.step5?.approved?.at(-1),
    active:
      !!stepLogs.step5?.approved?.at(-1) &&
      !!stepLogs.step4?.saved?.at(-1) &&
      !!stepLogs.step3?.approved?.at(-1),
  },
];

        // Stop at first card where active is false
        const visibleCards = [];
        for (const card of cards) {
          if (!card.active) break;
          visibleCards.push(card);
        }

        return visibleCards.map((card, i) => {
          const user = allUsers?.find((u) => u.id == card.entry.by);
          return (
            <div key={i} className="rfp-activity-pill">
              <div className="rfp-activity-icon">
                {user?.avatar
                  ? <img
                      src={`${configure.imgUrl}${user.avatar}`}
                      alt={user.name}
                      style={{ width: 28, height: 28, borderRadius: "50%", objectFit: "cover" }}
                    />
                  : <i className="fas fa-user" />}
              </div>
              <div>
                <div className="rfp-activity-label">
                  {card.label}{" "}
                  {card.sublabel && (
                    <span style={{ fontWeight: 400, textTransform: "none" }}>
                      {card.sublabel}
                    </span>
                  )}
                </div>
                <div className="rfp-activity-user">
                  {user?.name || `User #${card.entry.by}`}
                </div>
                <div className="rfp-activity-time">
                  {new Date(card.entry.at).toLocaleString()}
                </div>
              </div>
            </div>
          );
        });
      })()}
    </div>
  </div>
)}
        
    </div>
  );
};

export default StepTiming;
